import ConditionalToolTip from "components/design/conditionalToolTip";
import { Button } from "components/DesignSystem/Button/Button";
import { SwitchCase } from "components/DesignSystem/SwitchCase/SwitchCase";
import { AICategorised } from "components/icons/AICategorised";
import { AICategorisedAccepted } from "components/icons/AICategorisedAccepted";
import { RuleCategorised } from "components/icons/RuleCategorised";
import { FIXED_ASSET_CATEGORY_IDENTIFIER_PREFIX } from "constants/categoryIdentifier";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import { MouseEvent } from "react";
import { useDispatch } from "react-redux";
import { useUpdateTransactionMutation } from "store/apis/transactions";
import { openFixedAssetModal } from "store/slices/fixedAssetModal";
import { Transactions } from "types/Models/books";
import { BackendError } from "types/utils/error";
import { CategorisationStatusInfo } from "./CategorisationStatusInfo";
import { AICategorisedManually } from "components/icons/AICategorisedManually";
import { BrexCategorised } from "components/icons/BrexCategorised";

type CategoryStatusProps = {
  groupId: string;
  entityId: string;
  transaction: Transactions;
  disabled?: boolean;
};

const CategorisedIcon = {
  AI_CATEGORISED: () => (
    <span>
      <AICategorised />
    </span>
  ),
  AI_CATEGORISED_ACCEPTED: () => (
    <span>
      <AICategorisedAccepted />
    </span>
  ),
  MANUAL_CATEGORISED: () => (
    <span>
      <AICategorisedManually size="24" />
    </span>
  ),
  RULE_CATEGORIZED: () => (
    <span>
      <RuleCategorised />
    </span>
  ),
  BREX_CONFIRMED_CATEGORY: () => (
    <span>
      <BrexCategorised />
    </span>
  ),
  NOT_CATEGORISED: () => null,
};

export const CategoryStatus = ({
  entityId,
  groupId,
  transaction,
  disabled = false,
}: CategoryStatusProps) => {
  const dispatch = useDispatch();
  const [updateTransaction, { isLoading }] = useUpdateTransactionMutation();
  const { alertToast } = useToast();
  const categorisationStatusInfoModal = useModal();

  const { category, categorisation_status, uuid, amount, description, date } =
    transaction.transaction;

  const isAICategorised = categorisation_status === "AI_CATEGORISED";
  const CategorisationStatusIcon = categorisation_status
    ? CategorisedIcon[categorisation_status]
    : CategorisedIcon.NOT_CATEGORISED;

  const onCategoryReview = async (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    try {
      await updateTransaction({
        groupId,
        entityId,
        previewTxnId: uuid,
        payload: {
          ai_category_confirmation: true,
          transaction_category_uuid: category?.uuid || null,
        },
      }).unwrap();

      const isNegativeTransaction = amount < 0;
      const isFixedAssetCategory = category?.identifier
        ?.toString()
        .startsWith(FIXED_ASSET_CATEGORY_IDENTIFIER_PREFIX);

      if (isNegativeTransaction && isFixedAssetCategory) {
        dispatch(
          openFixedAssetModal({
            transactionId: uuid,
            description: description || "",
            amount,
            date: date || "",
          })
        );
      }
    } catch (error) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    }
  };

  return (
    <>
      <ConditionalToolTip
        condition={
          <SwitchCase
            value={categorisation_status}
            cases={{
              AI_CATEGORISED: (
                <div className="t-items-start t-flex t-flex-col t-text-start">
                  Categorised by AI Agent,
                  <br />
                  click to confirm
                  <br />
                  <br />
                  <button
                    className="all:unset t-text-body-sm t-text-white t-underline"
                    onClick={categorisationStatusInfoModal.open}
                  >
                    Tell me more
                  </button>
                </div>
              ),
              MANUAL_CATEGORISED: (
                <div className="t-items-start t-flex t-flex-col t-text-start">
                  Categorised Manually
                  <br />
                  <br />
                  <button
                    className="all:unset t-text-body-sm t-text-white t-underline"
                    onClick={categorisationStatusInfoModal.open}
                  >
                    Tell me more
                  </button>
                </div>
              ),
              RULE_CATEGORIZED: (
                <div className="t-items-start t-flex t-flex-col t-text-start">
                  Categorised by Rules
                  <br />
                  <br />
                  <button
                    className="all:unset t-text-body-sm t-text-white t-underline"
                    onClick={categorisationStatusInfoModal.open}
                  >
                    Tell me more
                  </button>
                </div>
              ),
              AI_CATEGORISED_ACCEPTED: (
                <div className="t-items-start t-flex t-flex-col t-text-start">
                  Categorised by AI Agent &
                  <br />
                  Manually Confirmed
                  <br />
                  <br />
                  <button
                    className="all:unset t-text-body-sm t-text-white t-underline"
                    onClick={categorisationStatusInfoModal.open}
                  >
                    Tell me more
                  </button>
                </div>
              ),
              BREX_CONFIRMED_CATEGORY: (
                <div className="t-items-start t-flex t-flex-col t-text-start">
                  Incoming category from Brex
                  <br />
                  <button
                    className="all:unset t-text-body-sm t-text-white t-underline"
                    onClick={categorisationStatusInfoModal.open}
                  >
                    Tell me more
                  </button>
                </div>
              ),
              NOT_CATEGORISED: "",
            }}
            default={null}
          />
        }
        side="right"
      >
        <span>
          <Button
            size="xsmall"
            customType="ghost_icon"
            onClick={(e) => {
              if (isAICategorised && !isLoading && !disabled) {
                onCategoryReview(e);
              }
            }}
            isLoading={isLoading}
          >
            <CategorisationStatusIcon />
          </Button>
        </span>
      </ConditionalToolTip>
      <CategorisationStatusInfo {...categorisationStatusInfoModal} />
    </>
  );
};
